import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useTier } from "@/context/TierContext";
import { getPlanCta } from "@/lib/planCta";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Sparkles, Lock, RefreshCw, AlertTriangle, Check, X, Pencil,
  Camera, ScanEye, ChevronRight, GitCompareArrows,
} from "lucide-react";

const OUTCOME_LABELS = {
  confirmed_difference: "Confirmed difference",
  no_difference: "No visible difference observed",
  unable_to_compare: "Unable to compare",
  retake_recommended: "Retake recommended",
};
const STATE_STYLE = {
  not_reviewed: "bg-slate-100 text-slate-600 border-slate-200",
  ai_suggestions_ready: "bg-blue-50 text-blue-700 border-blue-200",
  confirmed_difference: "bg-amber-50 text-amber-700 border-amber-200",
  no_difference: "bg-emerald-50 text-emerald-700 border-emerald-200",
  unable_to_compare: "bg-slate-50 text-slate-500 border-slate-200",
  retake_recommended: "bg-orange-50 text-orange-700 border-orange-200",
};
const STATE_LABEL = {
  not_reviewed: "Not reviewed",
  ai_suggestions_ready: "AI suggestions ready",
  ...OUTCOME_LABELS,
};
const QUALITY_LABEL = {
  retake: "Retake suggested", too_dark: "Darkness", dark: "Darkness", blurry: "Blur",
  blur: "Blur", glare: "Glare", too_close: "Framing", obstructed: "Obstruction",
};

export default function SelfAudit() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const { isPremium, trialing, trialEligible, openUpgrade } = useTier();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const { data } = await api.get(`/properties/${propertyId}/self-audit`);
      setData(data);
    } catch (e) {
      if (e?.response?.status === 404) { toast.error("Property not found."); navigate("/dashboard"); return; }
      setError(true);
    } finally { setLoading(false); }
  }, [propertyId, navigate]);

  useEffect(() => { load(); }, [load]);

  const runAnalysis = async () => {
    if (!isPremium && !trialing) { openUpgrade("self_audit"); return; }
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/properties/${propertyId}/self-audit/analyze`, {});
      setData(data);
      const run = data.run || {};
      if ((run.failed || []).length) {
        toast.warning(`Reviewed ${run.analyzed?.length || 0} pair(s); ${run.failed.length} couldn't be analyzed — retry available.`);
      } else {
        toast.success(`Analysis complete — ${(run.analyzed?.length || 0)} reviewed, ${(run.comparison_limited?.length || 0)} comparison-limited.`);
      }
    } catch (e) {
      if (e?.response?.status === 402) { openUpgrade("self_audit"); }
      else toast.error(e?.response?.data?.detail || "Couldn't run the review — please try again.");
    } finally { setAnalyzing(false); }
  };

  if (loading) return (
    <div className="min-h-screen grid place-items-center bg-[#F8FAFC]" data-testid="self-audit-loading">
      <Loader2 className="animate-spin text-brand" size={28} />
    </div>
  );
  if (error) return (
    <div className="min-h-screen grid place-items-center bg-[#F8FAFC] p-6" data-testid="self-audit-error">
      <div className="text-center">
        <AlertTriangle className="mx-auto text-amber-500" size={30} />
        <p className="mt-2 font-semibold text-navy">Self-audit couldn’t load.</p>
        <Button data-testid="self-audit-retry" onClick={load} className="mt-3 rounded-xl bg-navy text-white">
          <RefreshCw size={15} className="mr-1.5" /> Retry
        </Button>
      </div>
    </div>
  );

  const c = data.counts || {};
  const cta = getPlanCta({ trialEligible, trialing });
  const canRun = isPremium || trialing;

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-24" data-testid="self-audit-page">
      <header className="sticky top-0 z-10 bg-white border-b border-[#E2E8F0] px-4 py-3 flex items-center gap-2">
        <button data-testid="self-audit-back" onClick={() => navigate(`/property/${propertyId}?tab=moveout`)} className="text-navy"><ArrowLeft size={20} /></button>
        <div className="min-w-0">
          <h1 className="font-heading font-bold text-navy text-base leading-tight flex items-center gap-1.5"><ScanEye size={16} className="text-brand" /> Pre-Move-Out Self-Audit</h1>
          <p className="text-[11px] text-[#64748B]">Review possible visual differences before you seal</p>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        {data.empty ? (
          <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5 text-center" data-testid="self-audit-empty">
            <GitCompareArrows className="mx-auto text-[#94A3B8]" size={26} />
            <p className="mt-2 font-semibold text-navy text-sm">No confirmed pairs yet</p>
            <p className="text-xs text-[#64748B] mt-1">{data.empty_reason}</p>
            <Button data-testid="self-audit-go-proof-replay" onClick={() => navigate(`/property/${propertyId}?tab=moveout`)}
              className="mt-3 rounded-xl bg-navy text-white text-sm">Go to Proof Replay <ChevronRight size={15} className="ml-1" /></Button>
          </div>
        ) : (
          <>
            {/* Summary + run */}
            <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4" data-testid="self-audit-summary">
              <p className="font-heading text-lg font-bold text-navy" data-testid="self-audit-progress">
                Reviewed {c.reviewed} of {c.confirmed_pairs} confirmed pairs
              </p>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#475569]">
                <span data-testid="self-audit-awaiting">{c.awaiting_review} awaiting review</span>
                {c.retakes_recommended > 0 && <span data-testid="self-audit-retakes">{c.retakes_recommended} retake recommended</span>}
              </div>
              {canRun ? (
                <Button data-testid="self-audit-analyze" onClick={runAnalysis} disabled={analyzing}
                  className="mt-3 w-full h-11 rounded-xl bg-navy hover:bg-navy-hover text-white">
                  {analyzing ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Sparkles size={16} className="mr-2" />}
                  {analyzing ? "Analyzing paired photos…" : "Analyze paired photos"}
                </Button>
              ) : (
                <Button data-testid="self-audit-upgrade" onClick={() => openUpgrade("self_audit")}
                  className="mt-3 w-full h-11 rounded-xl bg-navy hover:bg-navy-hover text-white">
                  <Lock size={15} className="mr-2" /> {cta.cta}
                </Button>
              )}
              <p className="mt-3 text-[11px] text-[#94A3B8] leading-snug">{data.disclaimer}</p>
            </div>

            {data.pairs.map((p) => (
              <PairCard key={p.pair_id} pair={p} propertyId={propertyId} canRun={canRun} onChanged={load} navigate={navigate} />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function PairCard({ pair, propertyId, canRun, onChanged, navigate }) {
  const [editing, setEditing] = useState(null);   // index being edited
  const [editText, setEditText] = useState("");
  const [manual, setManual] = useState("");
  const [busy, setBusy] = useState(false);

  const state = pair.state;
  const draft = pair.ai_draft;

  const dismissSuggestion = async (index) => {
    setBusy(true);
    try {
      await api.post(`/properties/${propertyId}/self-audit/pairs/${pair.pair_id}/dismiss`, { observation_index: index });
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't dismiss that suggestion."); }
    finally { setBusy(false); }
  };

  const addObservation = async (text, source) => {
    setBusy(true);
    try {
      await api.post(`/properties/${propertyId}/self-audit/pairs/${pair.pair_id}/observations`, { text, source });
      toast.success("Observation saved."); onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that note.");
    } finally { setBusy(false); }
  };

  const removeObservation = async (obsId) => {
    setBusy(true);
    try {
      await api.delete(`/properties/${propertyId}/self-audit/pairs/${pair.pair_id}/observations/${obsId}`);
      onChanged();
    } catch { toast.error("Couldn't remove that note."); }
    finally { setBusy(false); }
  };

  const setOutcome = async (outcome) => {
    setBusy(true);
    try {
      await api.patch(`/properties/${propertyId}/self-audit/pairs/${pair.pair_id}/outcome`, { outcome });
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't set outcome."); }
    finally { setBusy(false); }
  };

  const chooseRetake = async () => {
    await setOutcome("retake_recommended");
    toast.info("Use Proof Replay to recapture this move-out photo.");
    navigate(`/property/${propertyId}?tab=moveout`);
  };

  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4" data-testid={`self-audit-pair-${pair.pair_id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-navy text-sm truncate">{pair.room_name}</p>
          {(pair.baseline_label || pair.move_out_label) && (
            <p className="text-[11px] text-[#64748B] truncate">{pair.move_out_label || pair.baseline_label}</p>
          )}
        </div>
        <span className={`text-[10px] font-bold px-2 py-1 rounded-full border ${STATE_STYLE[state] || STATE_STYLE.not_reviewed}`}
          data-testid={`self-audit-state-${pair.pair_id}`}>{STATE_LABEL[state] || "Not reviewed"}</span>
      </div>

      {/* Side-by-side viewer (reused pattern) */}
      <div className="mt-3 grid grid-cols-2 gap-2" data-testid={`self-audit-viewer-${pair.pair_id}`}>
        <div>
          <p className="text-[10px] font-semibold text-[#94A3B8] mb-1">Move-in baseline · sealed</p>
          <img src={fileUrl(pair.baseline_image)} alt="move-in baseline" className="aspect-square w-full object-cover rounded-lg border border-[#E2E8F0]" />
          <p className="text-[9px] text-[#94A3B8] mt-0.5">{pair.move_in_captured_at ? new Date(pair.move_in_captured_at).toLocaleDateString() : ""}</p>
        </div>
        <div>
          <p className="text-[10px] font-semibold text-[#94A3B8] mb-1">Move-out current</p>
          <img src={fileUrl(pair.move_out_image)} alt="move-out current" className="aspect-square w-full object-cover rounded-lg border border-[#E2E8F0]" />
          <p className="text-[9px] text-[#94A3B8] mt-0.5">{pair.move_out_captured_at ? new Date(pair.move_out_captured_at).toLocaleDateString() : ""}</p>
        </div>
      </div>

      {!pair.move_out_sealed && (
        <p className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5"
          data-testid={`self-audit-unsealed-${pair.pair_id}`}>
          <AlertTriangle size={10} /> Move-out photo not sealed yet
        </p>
      )}

      {/* Quality warnings (from existing deterministic flags) */}
      {(() => {
        const qw = [...new Set([...(pair.quality_warnings?.baseline || []), ...(pair.quality_warnings?.move_out || [])])];
        if (!qw.length) return null;
        return (
          <div className="mt-2 flex flex-wrap gap-1" data-testid={`self-audit-quality-${pair.pair_id}`}>
            {qw.map((f) => (
              <span key={f} className="text-[9px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                {QUALITY_LABEL[f] || f}
              </span>
            ))}
          </div>
        );
      })()}

      {pair.needs_reanalysis && (
        <p className="mt-2 text-[11px] text-[#64748B]" data-testid={`self-audit-stale-${pair.pair_id}`}>
          This pair changed — run analysis again for fresh suggestions.
        </p>
      )}

      {/* AI draft suggestions */}
      {draft && (
        <div className="mt-3 rounded-xl border border-blue-100 bg-blue-50/50 p-3" data-testid={`self-audit-draft-${pair.pair_id}`}>
          <p className="text-[10px] font-bold uppercase tracking-wide text-blue-700 flex items-center gap-1">
            <Sparkles size={11} /> AI suggestion — draft (needs your confirmation)
          </p>
          {draft.comparable === false ? (
            <p className="mt-1.5 text-xs text-[#475569]" data-testid={`self-audit-notcomparable-${pair.pair_id}`}>
              {draft.limited ? "Comparison limited. " : ""}{draft.reason || "Photos may not be comparable."}
            </p>
          ) : (draft.observations || []).length === 0 ? (
            <p className="mt-1.5 text-xs text-[#475569]">No possible differences suggested.</p>
          ) : (
            <div className="mt-2 space-y-2">
              {(draft.observations || []).map((o, i) => o.dismissed ? (
                <p key={i} className="text-[11px] text-[#94A3B8] italic" data-testid={`self-audit-rejected-${pair.pair_id}-${i}`}>Suggestion dismissed.</p>
              ) : (
                <div key={i} className="rounded-lg border border-blue-100 bg-white p-2" data-testid={`self-audit-suggestion-${pair.pair_id}-${i}`}>
                  <p className="text-[10px] font-semibold text-[#94A3B8]">{o.region}</p>
                  {editing === i ? (
                    <>
                      <textarea data-testid={`self-audit-edit-input-${pair.pair_id}-${i}`} value={editText} onChange={(e) => setEditText(e.target.value)}
                        className="mt-1 w-full text-xs border border-[#E2E8F0] rounded-lg p-2" rows={3} />
                      <div className="mt-1 flex gap-1.5">
                        <button data-testid={`self-audit-edit-save-${pair.pair_id}-${i}`} disabled={busy}
                          onClick={async () => { await addObservation(editText, "ai_confirmed"); setEditing(null); }}
                          className="text-[11px] font-semibold text-white bg-navy rounded px-2 py-1">Save observation</button>
                        <button onClick={() => setEditing(null)} className="text-[11px] text-[#64748B] px-2 py-1">Cancel</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="mt-0.5 text-xs text-[#334155]">{o.description}</p>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        <button data-testid={`self-audit-confirm-${pair.pair_id}-${i}`} disabled={busy}
                          onClick={() => addObservation(o.description, "ai_confirmed")}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                          <Check size={11} /> Confirm</button>
                        <button data-testid={`self-audit-edit-${pair.pair_id}-${i}`}
                          onClick={() => { setEditing(i); setEditText(o.description); }}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#475569] bg-slate-50 border border-slate-200 rounded px-2 py-1">
                          <Pencil size={11} /> Edit</button>
                        <button data-testid={`self-audit-reject-${pair.pair_id}-${i}`} disabled={busy}
                          onClick={() => dismissSuggestion(i)}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#94A3B8] bg-slate-50 border border-slate-200 rounded px-2 py-1">
                          <X size={11} /> Reject</button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Confirmed observations (user data) */}
      {(pair.confirmed_observations || []).length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid={`self-audit-confirmed-${pair.pair_id}`}>
          {pair.confirmed_observations.map((o) => (
            <div key={o.obs_id} className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-2 flex items-start gap-2" data-testid={`self-audit-confirmed-obs-${o.obs_id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-[9px] font-bold uppercase tracking-wide text-emerald-700">
                  {o.source === "user" ? "Your observation" : "Confirmed from AI suggestion"}
                </p>
                <p className="text-xs text-[#334155] mt-0.5">{o.text}</p>
              </div>
              <button data-testid={`self-audit-remove-obs-${o.obs_id}`} onClick={() => removeObservation(o.obs_id)} className="text-[#94A3B8] hover:text-red-500 shrink-0"><X size={13} /></button>
            </div>
          ))}
        </div>
      )}

      {/* Manual observation (no AI) */}
      <div className="mt-3">
        <textarea data-testid={`self-audit-manual-input-${pair.pair_id}`} value={manual} onChange={(e) => setManual(e.target.value)}
          placeholder="Add your own factual observation (what is visible) — no AI needed"
          className="w-full text-xs border border-[#E2E8F0] rounded-lg p-2" rows={2} />
        <Button data-testid={`self-audit-manual-add-${pair.pair_id}`} size="sm" variant="outline" disabled={busy || !manual.trim()}
          onClick={async () => { await addObservation(manual.trim(), "user"); setManual(""); }}
          className="mt-1 h-8 rounded-lg text-xs">Add observation</Button>
      </div>

      {/* Outcome */}
      <div className="mt-3 border-t border-[#F1F5F9] pt-3">
        <p className="text-[10px] font-bold uppercase tracking-wide text-[#94A3B8]">Review outcome</p>
        <div className="mt-1.5 grid grid-cols-2 gap-1.5">
          <OutcomeBtn tid={`self-audit-outcome-confirmed_difference-${pair.pair_id}`} active={pair.outcome === "confirmed_difference"} disabled={busy} onClick={() => setOutcome("confirmed_difference")}>Confirm visible difference</OutcomeBtn>
          <OutcomeBtn tid={`self-audit-outcome-no_difference-${pair.pair_id}`} active={pair.outcome === "no_difference"} disabled={busy} onClick={() => setOutcome("no_difference")}>No visible difference</OutcomeBtn>
          <OutcomeBtn tid={`self-audit-outcome-unable_to_compare-${pair.pair_id}`} active={pair.outcome === "unable_to_compare"} disabled={busy} onClick={() => setOutcome("unable_to_compare")}>Unable to compare</OutcomeBtn>
          <OutcomeBtn tid={`self-audit-outcome-retake_recommended-${pair.pair_id}`} active={pair.outcome === "retake_recommended"} disabled={busy} onClick={chooseRetake}><Camera size={12} className="mr-1 inline" />Retake move-out photo</OutcomeBtn>
        </div>
      </div>
    </div>
  );
}

function OutcomeBtn({ children, active, onClick, disabled, tid }) {
  return (
    <button data-testid={tid} onClick={onClick} disabled={disabled}
      className={`text-[11px] font-semibold rounded-lg border px-2 py-2 text-center ${active ? "border-navy bg-navy text-white" : "border-[#E2E8F0] bg-white text-[#475569] hover:bg-[#F8FAFC]"}`}>
      {children}
    </button>
  );
}
